import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { PaperPlaneRight, MapPin, Warning, ShieldCheck, ArrowClockwise, Camera, VideoCamera, Images, X, CircleNotch } from "@phosphor-icons/react";
import { useChatAlerts } from "@/hooks/useChatAlerts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUS_LABEL = { menunggu: "Menunggu", siap_berangkat: "Siap Berangkat", dalam_perjalanan: "Dalam Perjalanan", tiba: "Sudah Tiba", selesai: "Layanan Selesai" };
const MAPS_RE = /(google\.[a-z.]+\/maps|maps\.google\.[a-z.]+|maps\.app\.goo\.gl|goo\.gl\/maps)/i;
const VID_MAX = 30 * 1024 * 1024;

// Kompres foto di sisi klien (resize + JPEG) — juga membuang metadata EXIF/GPS via canvas.
async function compressImage(file) {
  try {
    const url = URL.createObjectURL(file);
    const img = document.createElement("img");
    await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = url; });
    let { width, height } = img;
    const max = 1600;
    if (Math.max(width, height) > max) { const s = max / Math.max(width, height); width = Math.round(width * s); height = Math.round(height * s); }
    const cv = document.createElement("canvas"); cv.width = width; cv.height = height;
    cv.getContext("2d").drawImage(img, 0, 0, width, height);
    URL.revokeObjectURL(url);
    const blob = await new Promise((r) => cv.toBlob(r, "image/jpeg", 0.72));
    if (!blob) return file;
    return new File([blob], (file.name || "foto").replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg" });
  } catch { return file; }
}

// Klien mengakses via ?t=<token>; petugas/admin via JWT staff (localStorage token).
export default function HomecareChat() {
  const { roomId } = useParams();
  const [sp] = useSearchParams();
  const token = sp.get("t") || "";
  const isStaff = !token;
  const nav = useNavigate();
  const [room, setRoom] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [err, setErr] = useState("");
  const [geoErr, setGeoErr] = useState("");
  const [showMaps, setShowMaps] = useState(false);
  const [mapsLink, setMapsLink] = useState("");
  const [media, setMedia] = useState({});        // id -> objectURL | "loading" | "error"
  const [preview, setPreview] = useState(null);   // {file, url, kind}
  const [uploading, setUploading] = useState(false);
  const [upErr, setUpErr] = useState("");
  const [picker, setPicker] = useState(null);     // "photo" | "video" | null
  const bottom = useRef(null);
  const imgCamInput = useRef(null);   // kamera (capture) — Ambil Foto
  const imgGalInput = useRef(null);   // galeri (TANPA capture — penting utk iPhone)
  const vidCamInput = useRef(null);   // kamera (capture) — Ambil Video
  const vidGalInput = useRef(null);   // galeri (TANPA capture)
  const fetching = useRef({});

  const client = useCallback(() => {
    const headers = {};
    if (token) headers["X-Room-Token"] = token;
    else { const jwt = localStorage.getItem("token"); if (jwt) headers.Authorization = `Bearer ${jwt}`; }
    return axios.create({ baseURL: API, headers });
  }, [token]);

  const load = useCallback(async () => {
    try {
      const c = client();
      const [r, m] = await Promise.all([c.get(`/homecare/rooms/${roomId}`), c.get(`/homecare/rooms/${roomId}/messages`)]);
      setRoom(r.data.room); setMsgs(m.data.messages); setErr("");
      if (!document.hidden) c.post(`/homecare/rooms/${roomId}/read`).catch(() => {});
    } catch (e) { setErr(e?.response?.data?.detail || "Tidak dapat memuat ruang chat."); }
  }, [client, roomId]);

  useEffect(() => { load(); const id = setInterval(() => { if (!document.hidden) load(); }, 5000); return () => clearInterval(id); }, [load]);
  useEffect(() => { bottom.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  // Ambil blob media (ber-otorisasi via header) -> objectURL; TIDAK ada URL publik.
  const fetchMedia = useCallback(async (m) => {
    if (fetching.current[m.id]) return;
    fetching.current[m.id] = true;
    setMedia((p) => ({ ...p, [m.id]: "loading" }));
    try {
      const r = await client().get(`/homecare/rooms/${roomId}/media/${m.id}`, { responseType: "blob" });
      setMedia((p) => ({ ...p, [m.id]: URL.createObjectURL(r.data) }));
    } catch {
      setMedia((p) => ({ ...p, [m.id]: "error" }));
      fetching.current[m.id] = false;
    }
  }, [client, roomId]);

  useEffect(() => {
    msgs.forEach((m) => { if ((m.type === "image" || m.type === "video") && !media[m.id] && !fetching.current[m.id]) fetchMedia(m); });
  }, [msgs, media, fetchMedia]);

  const isMine = useCallback((m) => (isStaff ? m.sender_kind === "staff" : m.sender_kind === "client"), [isStaff]);
  const { titleUnread } = useChatAlerts(msgs, isMine);
  useEffect(() => { document.title = `${titleUnread > 0 ? `(${titleUnread}) ` : ""}Chat Homecare — GBS`; }, [titleUnread]);

  const send = async () => {
    const t = text.trim(); if (!t) return;
    try { await client().post(`/homecare/rooms/${roomId}/messages`, { type: "text", text: t }); setText(""); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal mengirim pesan"); }
  };
  const postLoc = async (payload) => {
    try { await client().post(`/homecare/rooms/${roomId}/messages`, { type: "location", ...payload }); setGeoErr(""); setShowMaps(false); setMapsLink(""); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal berbagi lokasi"); }
  };
  const shareLocation = () => {
    if (!navigator.geolocation) return toast.error("Perangkat tidak mendukung lokasi");
    setGeoErr("");
    const ok = (pos) => postLoc({ lat: pos.coords.latitude, lng: pos.coords.longitude, label: "Lokasi saya" });
    navigator.geolocation.getCurrentPosition(ok, (e1) => {
      if (e1.code === 1) { setGeoErr("denied"); setShowMaps(true); return; }
      navigator.geolocation.getCurrentPosition(ok, (e2) => { setGeoErr(e2.code === 1 ? "denied" : "fail"); setShowMaps(true); },
        { enableHighAccuracy: false, timeout: 15000, maximumAge: 60000 });
    }, { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 });
  };
  const sendMapsLink = () => {
    const url = mapsLink.trim();
    if (!/^https?:\/\//i.test(url) || !MAPS_RE.test(url)) return toast.error("Tempel tautan Google Maps yang valid.");
    postLoc({ url, label: "Lokasi (link)" });
  };

  // ---- Foto & Video ----
  const openPicker = (kind) => setPicker(kind);
  const pick = (which) => {
    // which: "camera" | "gallery"; TANPA capture untuk galeri agar iPhone tidak memaksa kamera.
    const ref = picker === "photo" ? (which === "camera" ? imgCamInput : imgGalInput) : (which === "camera" ? vidCamInput : vidGalInput);
    setPicker(null);
    ref.current?.click();
  };
  const onPick = async (e, kind) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    setUpErr("");
    if (kind === "image") {
      const f = await compressImage(file);
      setPreview({ file: f, url: URL.createObjectURL(f), kind: "image" });
    } else {
      if (file.size > VID_MAX) return toast.error("Ukuran video melebihi 30 MB. Pilih video lebih pendek.");
      setPreview({ file, url: URL.createObjectURL(file), kind: "video" });
    }
  };
  const cancelPreview = () => { if (preview?.url) URL.revokeObjectURL(preview.url); setPreview(null); setUpErr(""); };
  const sendMedia = async () => {
    if (!preview) return;
    setUploading(true); setUpErr("");
    try {
      const fd = new FormData(); fd.append("file", preview.file);
      await client().post(`/homecare/rooms/${roomId}/media`, fd);
      cancelPreview(); await load();
    } catch (e) { setUpErr(e?.response?.data?.detail || "Gagal mengunggah. Coba lagi."); }
    finally { setUploading(false); }
  };

  const setStatus = async (st) => {
    try { await client().post(`/homecare/rooms/${roomId}/service-status`, { service_status: st }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal ubah status"); }
  };
  const emergency = async () => {
    try { await client().post(`/homecare/rooms/${roomId}/emergency`, {}); toast.success("Admin GBS telah diberi tahu."); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal mengirim darurat"); }
  };

  if (err && !room) return <div className="mx-auto mt-16 max-w-sm rounded-2xl border border-pink-100 bg-white p-6 text-center text-sm text-slate-600" data-testid="hc-error">{err}</div>;
  if (!room) return <div className="mt-20 text-center text-sm text-slate-400">Memuat…</div>;
  const closed = room.status !== "open";

  const renderMedia = (m) => {
    const u = media[m.id];
    if (!u || u === "loading") return <div className="flex h-32 w-40 items-center justify-center rounded-xl bg-black/5 text-[11px] text-slate-400" data-testid={`hc-media-loading-${m.id}`}><CircleNotch size={18} className="animate-spin" /></div>;
    if (u === "error") return (
      <button data-testid={`hc-media-retry-${m.id}`} onClick={() => { fetching.current[m.id] = false; fetchMedia(m); }} className="flex h-24 w-40 flex-col items-center justify-center gap-1 rounded-xl bg-black/5 text-[11px] font-bold text-[#6A38C7]"><ArrowClockwise size={16} weight="bold" /> Muat ulang</button>
    );
    if (m.type === "image") return <img data-testid={`hc-media-image-${m.id}`} src={u} alt="foto" onClick={() => window.open(u, "_blank")} className="max-h-64 max-w-full cursor-pointer rounded-xl object-cover" />;
    return <video data-testid={`hc-media-video-${m.id}`} src={u} controls playsInline className="max-h-72 max-w-full rounded-xl" />;
  };

  return (
    <div className="mx-auto flex h-[100dvh] max-w-md flex-col bg-[#FBF7FF]" data-testid="homecare-chat">
      {isStaff && (
        <div className="border-b border-purple-100 bg-white/95 px-4 py-2 backdrop-blur">
          <button data-testid="hc-chat-back-dashboard" onClick={() => nav("/jadwal")} className="inline-flex items-center gap-1 text-xs font-bold text-[#6A38C7] transition-transform active:scale-95">
            ← Kembali ke Dashboard
          </button>
        </div>
      )}
      <header className="flex items-center gap-3 bg-gradient-to-r from-[#4C1D95] to-[#6A38C7] px-4 py-3 text-white">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/20 font-bold">{(isStaff ? room.client_name : room.assigned_staff_name || "?").slice(0, 1)}</div>
        <div className="min-w-0 flex-1">
          <p className="truncate font-heading text-sm font-extrabold" data-testid="hc-peer-name">{isStaff ? room.client_name : (room.assigned_staff_name || "Petugas GBS")}</p>
          <p className="text-[11px] text-white/80">Homecare • {room.service || "Layanan GBS"}</p>
        </div>
        <span className="rounded-full bg-white/20 px-2 py-0.5 text-[10px] font-bold" data-testid="hc-service-status">{STATUS_LABEL[room.service_status] || "Menunggu"}</span>
      </header>

      <div className="flex items-center gap-2 bg-amber-50 px-3 py-1.5 text-[11px] text-amber-800"><ShieldCheck size={14} weight="bold" /> Gunakan chat resmi GBS untuk menjaga keamanan dan kenyamanan pelayanan.</div>
      {isStaff && (
        <div className="flex flex-wrap gap-1.5 border-b border-purple-100 bg-white px-3 py-2">
          {["siap_berangkat", "dalam_perjalanan", "tiba", "selesai"].map((s) => (
            <button key={s} data-testid={`hc-status-${s}`} disabled={closed} onClick={() => setStatus(s)} className="rounded-full bg-[#EEE6FF] px-2.5 py-1 text-[11px] font-bold text-[#6A38C7] disabled:opacity-40">{STATUS_LABEL[s]}</button>
          ))}
          {room.alamat && <p className="mt-1 w-full break-words text-[11px] text-slate-500">Alamat: {room.alamat}</p>}
        </div>
      )}

      <div className="flex-1 space-y-2 overflow-y-auto px-3 py-3" data-testid="hc-messages">
        {msgs.map((m) => {
          const mine = isStaff ? (m.sender_kind === "staff" || m.sender_kind === "admin") : m.sender_kind === "client";
          if (m.sender_kind === "system") return <div key={m.id} className="mx-auto rounded-full bg-slate-100 px-3 py-1 text-center text-[11px] text-slate-500" data-testid="hc-system-msg">{m.text}</div>;
          const isMedia = m.type === "image" || m.type === "video";
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] min-w-0 break-words rounded-2xl px-3 py-2 text-base ${mine ? "bg-[#6A38C7] text-white" : "bg-white text-slate-700 border border-purple-100"} ${isMedia ? "p-1.5" : ""}`}>
                {!mine && <p className={`mb-0.5 text-[10px] font-bold opacity-70 ${isMedia ? "px-1.5 pt-1" : ""}`}>{m.sender_name}</p>}
                {m.type === "location" ? (
                  <a data-testid="hc-open-location" className={`mt-0.5 inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-bold ${mine ? "bg-white/20 text-white" : "bg-[#EEE6FF] text-[#6A38C7]"}`} target="_blank" rel="noreferrer" href={m.location?.url || `https://maps.google.com/?q=${m.location?.lat},${m.location?.lng}`}><MapPin size={14} weight="fill" /> Buka Lokasi</a>
                ) : isMedia ? renderMedia(m) : m.text}
                <p className={`mt-0.5 text-[9px] ${isMedia ? "px-1.5 pb-0.5" : ""} ${mine ? "text-white/70" : "text-slate-400"}`}>{(m.created_at_wita || "").slice(11, 16)} WITA</p>
              </div>
            </div>
          );
        })}
        <div ref={bottom} />
      </div>

      {geoErr && !closed && (
        <div className="border-t border-amber-100 bg-amber-50 px-3 py-2 text-[11px] text-amber-800" data-testid="hc-geo-help">
          {geoErr === "denied"
            ? "Izin lokasi ditolak. Di Safari iPhone: buka Pengaturan → Safari → Lokasi → Izinkan, atau ketuk ikon 'aA' di address bar → Pengaturan Situs → Lokasi → Izinkan. Lalu Coba Lagi, atau tempel tautan Google Maps di bawah."
            : "Gagal mendapatkan lokasi (GPS lambat/tidak tersedia). Coba Lagi, atau tempel tautan Google Maps di bawah."}
          <button data-testid="hc-geo-retry" onClick={shareLocation} className="ml-2 inline-flex items-center gap-1 rounded-full bg-[#EEE6FF] px-2 py-0.5 font-bold text-[#6A38C7]"><ArrowClockwise size={12} weight="bold" /> Coba Lagi</button>
        </div>
      )}
      {showMaps && !closed && (
        <div className="flex items-center gap-2 border-t border-purple-100 bg-white px-2 py-2" data-testid="hc-maps-row">
          <input data-testid="hc-maps-input" value={mapsLink} onChange={(e) => setMapsLink(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendMapsLink()} placeholder="Tempel tautan Google Maps…" className="min-w-0 flex-1 rounded-full border border-purple-100 bg-[#FDFBFE] px-3 py-2 text-base outline-none" />
          <button data-testid="hc-send-maps-link" onClick={sendMapsLink} className="rounded-full bg-[#6A38C7] px-3 py-2 text-xs font-bold text-white">Kirim</button>
        </div>
      )}

      {preview && (
        <div className="fixed inset-0 z-50 flex flex-col bg-black/80 p-4" data-testid="hc-media-preview">
          <div className="flex items-center justify-between text-white">
            <p className="text-sm font-bold">Pratinjau {preview.kind === "image" ? "Foto" : "Video"}</p>
            <button data-testid="hc-media-cancel" onClick={cancelPreview} disabled={uploading} className="rounded-full bg-white/15 p-1.5 disabled:opacity-40"><X size={18} weight="bold" /></button>
          </div>
          <div className="flex flex-1 items-center justify-center overflow-hidden py-4">
            {preview.kind === "image"
              ? <img src={preview.url} alt="pratinjau" className="max-h-full max-w-full rounded-xl object-contain" />
              : <video src={preview.url} controls playsInline className="max-h-full max-w-full rounded-xl" />}
          </div>
          {upErr && <p className="mb-2 text-center text-[12px] font-semibold text-pink-300" data-testid="hc-media-uperr">{upErr}</p>}
          <button data-testid="hc-media-send" onClick={sendMedia} disabled={uploading} className="flex items-center justify-center gap-2 rounded-full bg-[#6A38C7] py-3 text-sm font-bold text-white disabled:opacity-60">
            {uploading ? <><CircleNotch size={18} className="animate-spin" /> Mengunggah…</> : <>{upErr ? "Coba Kirim Ulang" : "Kirim"}</>}
          </button>
        </div>
      )}

      {picker && !closed && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40" onClick={() => setPicker(null)} data-testid="hc-media-picker">
          <div className="w-full max-w-md rounded-t-3xl bg-white p-4 pb-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <p className="mb-3 text-center text-sm font-extrabold text-[#4C1D95]">{picker === "photo" ? "Foto" : "Video"}</p>
            <div className="space-y-2">
              <button data-testid={picker === "photo" ? "hc-pick-photo-camera" : "hc-pick-video-camera"} onClick={() => pick("camera")}
                className="flex w-full items-center gap-3 rounded-2xl bg-[#EEE6FF] px-4 py-3 text-sm font-bold text-[#6A38C7] transition-transform active:scale-[.99]">
                {picker === "photo" ? <Camera size={20} weight="fill" /> : <VideoCamera size={20} weight="fill" />}
                Ambil {picker === "photo" ? "Foto" : "Video"}
              </button>
              <button data-testid={picker === "photo" ? "hc-pick-photo-gallery" : "hc-pick-video-gallery"} onClick={() => pick("gallery")}
                className="flex w-full items-center gap-3 rounded-2xl bg-[#EEE6FF] px-4 py-3 text-sm font-bold text-[#6A38C7] transition-transform active:scale-[.99]">
                <Images size={20} weight="fill" /> Pilih dari Galeri
              </button>
              <button data-testid="hc-pick-cancel" onClick={() => setPicker(null)}
                className="w-full rounded-2xl bg-slate-100 px-4 py-3 text-sm font-bold text-slate-500">Batal</button>
            </div>
          </div>
        </div>
      )}

      {closed && <div className="bg-slate-100 px-3 py-2 text-center text-xs text-slate-500" data-testid="hc-closed">{room.status === "archived" ? "Layanan telah selesai. Room Chat ini telah diarsipkan dan hanya dapat dibaca." : "Ruang chat telah ditutup. Hubungi Admin GBS bila perlu."}</div>}
      <input ref={imgGalInput} type="file" accept="image/*" hidden onChange={(e) => onPick(e, "image")} data-testid="hc-input-photo" />
      <input ref={imgCamInput} type="file" accept="image/*" capture="environment" hidden onChange={(e) => onPick(e, "image")} data-testid="hc-input-photo-camera" />
      <input ref={vidGalInput} type="file" accept="video/*" hidden onChange={(e) => onPick(e, "video")} data-testid="hc-input-video" />
      <input ref={vidCamInput} type="file" accept="video/*" capture="environment" hidden onChange={(e) => onPick(e, "video")} data-testid="hc-input-video-camera" />
      <div className="flex items-center gap-1.5 border-t border-purple-100 bg-white px-2 py-2">
        <button data-testid="hc-attach-photo" disabled={closed} onClick={() => openPicker("photo")} className="rounded-full bg-[#EEE6FF] p-2 text-[#6A38C7] disabled:opacity-40"><Camera size={18} weight="fill" /></button>
        <button data-testid="hc-attach-video" disabled={closed} onClick={() => openPicker("video")} className="rounded-full bg-[#EEE6FF] p-2 text-[#6A38C7] disabled:opacity-40"><VideoCamera size={18} weight="fill" /></button>
        <button data-testid="hc-share-location" disabled={closed} onClick={shareLocation} className="rounded-full bg-[#EEE6FF] p-2 text-[#6A38C7] disabled:opacity-40"><MapPin size={18} weight="fill" /></button>
        <input data-testid="hc-input" disabled={closed} value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Tulis pesan…" className="min-w-0 flex-1 rounded-full border border-purple-100 bg-[#FDFBFE] px-3 py-2 text-base outline-none disabled:opacity-40" />
        <button data-testid="hc-send" disabled={closed} onClick={send} className="rounded-full bg-[#6A38C7] p-2 text-white disabled:opacity-40"><PaperPlaneRight size={18} weight="fill" /></button>
      </div>
      <button data-testid="hc-emergency" onClick={emergency} className="flex items-center justify-center gap-1.5 bg-[#DB2777] py-2 text-sm font-bold text-white"><Warning size={16} weight="fill" /> Hubungi Admin GBS (Darurat)</button>
    </div>
  );
}
